import { forwardRef, useMemo, useState } from 'react'

import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { CountriesPhoneList } from 'shared/constants/countries'
import { PHONE_NUMBER } from 'shared/constants/regex'
import { useDialogBottomSheet } from 'shared/ui/dialog-bottom-sheet/lib'

import { ImageLazy } from '../image-lazy'

import { SelectCountrySheet } from './components/select-country-sheet'
import s from './styles.module.scss'

export interface IPhoneInput {
  phone: string
  code: string
}

export interface IPhoneCountry {
  dial_code: string
  code: string
  flag: string
  name: string
  length: number
}

interface Props {
  value?: IPhoneInput
  placeholder?: string
  error?: FieldErrors<IPhoneInput>
  onChange: (value: IPhoneInput) => void
  label?: string
  variant?: 'default' | 'secondary'
  disabled?: boolean
}

export const PhoneInput = forwardRef<HTMLInputElement, Props>(
  (
    {
      value,
      placeholder,
      error,
      onChange,
      variant = 'default',
      label,
      disabled = false,
    },
    ref,
  ) => {
    const { t } = useTranslation()

    const { openDialog } = useDialogBottomSheet()

    const localeCountries = useMemo(
      () =>
        CountriesPhoneList?.map((country) => ({
          ...country,
          name: t(`country_phone.${country.name}`),
        })),
      [],
    )

    const [currentPhoneMask, setCurrentPhoneMask] = useState<
      IPhoneCountry | undefined
    >(localeCountries.find((country) => country.code === 'KG'))

    const showBottomSheet = () => {
      if (disabled) {
        return
      }

      openDialog({
        child: (
          <SelectCountrySheet
            setCurrentPhoneMask={(phone) => {
              setCurrentPhoneMask(phone)
              onChange({ code: phone.code, phone: '' })
            }}
            currentCountry={currentPhoneMask}
            localeCountries={localeCountries}
          />
        ),
      })
    }

    const onChangeInputNumber = (phone: string) => {
      const clean = phone
        .replace(PHONE_NUMBER, '')
        ?.slice(0, currentPhoneMask?.length)

      onChange({
        phone: clean,
        code: String(currentPhoneMask?.dial_code),
      })
    }

    return (
      <div className={s.box}>
        {label && (
          <span className={clsx('semi-bold-14', s.label)}>{label}</span>
        )}
        <div className={clsx(s[variant], error && s.errorContainer)}>
          <div
            className={s.countryCodeField}
            onClick={showBottomSheet}
          >
            <ImageLazy
              alt="flag"
              src={currentPhoneMask?.flag}
              className={s.countryIcon}
            />

            <p className={s.countryCode}>{currentPhoneMask?.dial_code}</p>
          </div>

          <input
            ref={ref}
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={value?.phone}
            disabled={disabled}
            placeholder={placeholder || t('detail_ticket.input_phone')}
            onChange={(event) =>
              onChangeInputNumber(String(event.currentTarget.value))
            }
          />
        </div>
      </div>
    )
  },
)
